import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { MobileNav } from '../components/MobileNav';
import { 
  Sparkles, CalendarIcon, Send, Phone, Mail, 
  User, MessageSquare, Loader2, CheckCircle, Heart
} from 'lucide-react';
import { Footer } from '../components/Footer';
import SEO, { pageSEO } from '../components/SEO';

// Floating Gold Hearts Component
const FloatingGoldHearts = () => (
  <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
    {[...Array(12)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
        }}
        animate={{
          y: [0, -20, 0],
          opacity: [0.5, 0.8, 0.5],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 4 + Math.random() * 3,
          repeat: Infinity,
          delay: Math.random() * 2,
        }}
      >
        <Heart 
          className="text-[#B8860B] fill-[#D4AF37]" 
          style={{ 
            width: `${16 + Math.random() * 20}px`,
            height: `${16 + Math.random() * 20}px`,
            opacity: 0.6 + Math.random() * 0.3
          }} 
        />
      </motion.div>
    ))}
  </div>
);
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    birthday: null,
    note: ''
  });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email || !formData.birthday) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        birthday: format(formData.birthday, 'yyyy-MM-dd')
      });
      setSubmitted(true);
      toast.success('Thank you! Karine will be in touch soon.');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen swirl-bg flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="ethereal-card rounded-3xl p-12 text-center max-w-md"
        >
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="font-mystical text-3xl text-[#2D1B3D] font-bold mb-4">Thank You!</h2>
          <p className="text-[#4A3A5A] text-lg mb-8">
            Your information has been received. Karine will be in touch with you soon!
          </p>
          <Link to="/">
            <Button className="rounded-full btn-magic px-8 py-4 font-bold">
              Back to Home
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen swirl-bg">
      <SEO {...pageSEO.contact} />
      <FloatingGoldHearts />
      <MobileNav />

      <main className="max-w-2xl mx-auto px-6 md:px-12 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 via-amber-400 to-pink-500 mb-6 glow-purple">
            <MessageSquare className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-mystical text-4xl md:text-5xl text-[#2D1B3D] font-bold mb-4">
            Get in Touch with Karine
          </h1>
          <p className="text-[#4A3A5A] text-lg">
            Have questions or ready to book a reading? Fill out the form below and I'll call you back!
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="ethereal-card rounded-3xl p-8 md:p-10"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div className="space-y-2">
              <Label className="text-[#3D2A4D] font-semibold text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-[#8B5CF6]" />
                Full Name *
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Your full name"
                className="bg-white/70 border-purple-200 focus:border-[#8B5CF6] rounded-xl h-14 text-[#2D1B3D] text-lg font-medium placeholder:text-[#8C7A9C]"
                data-testid="contact-name-input"
                required
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label className="text-[#3D2A4D] font-semibold text-lg flex items-center gap-2">
                <Phone className="w-5 h-5 text-[#8B5CF6]" />
                Phone Number *
              </Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="bg-white/70 border-purple-200 focus:border-[#8B5CF6] rounded-xl h-14 text-[#2D1B3D] text-lg font-medium placeholder:text-[#8C7A9C]"
                data-testid="contact-phone-input"
                required
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label className="text-[#3D2A4D] font-semibold text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#8B5CF6]" />
                Email Address *
              </Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="your@email.com"
                className="bg-white/70 border-purple-200 focus:border-[#8B5CF6] rounded-xl h-14 text-[#2D1B3D] text-lg font-medium placeholder:text-[#8C7A9C]"
                data-testid="contact-email-input"
                required
              />
            </div>

            {/* Birthday */}
            <div className="space-y-2">
              <Label className="text-[#3D2A4D] font-semibold text-lg flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-[#8B5CF6]" />
                Your Birthday *
              </Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full h-14 justify-start text-left font-medium bg-white/70 border-purple-200 hover:border-[#8B5CF6] rounded-xl text-lg"
                    data-testid="contact-birthday-trigger"
                  >
                    <CalendarIcon className="mr-3 h-5 w-5 text-[#8B5CF6]" />
                    {formData.birthday ? (
                      <span className="text-[#2D1B3D]">{format(formData.birthday, 'MMMM d, yyyy')}</span>
                    ) : (
                      <span className="text-[#8C7A9C]">Select your birthday</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-purple-200" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.birthday}
                    onSelect={(date) => {
                      handleChange('birthday', date);
                      setCalendarOpen(false);
                    }}
                    disabled={(date) => date > new Date() || date < new Date('1920-01-01')}
                    captionLayout="dropdown-buttons"
                    fromYear={1920}
                    toYear={new Date().getFullYear()}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Note */}
            <div className="space-y-2">
              <Label className="text-[#3D2A4D] font-semibold text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#8B5CF6]" />
                Message (Optional)
              </Label>
              <Textarea
                value={formData.note}
                onChange={(e) => handleChange('note', e.target.value)}
                placeholder="Tell Karine what you're looking for... Are you seeking love? Closure from past relationships? Spiritual guidance?"
                className="bg-white/70 border-purple-200 focus:border-[#8B5CF6] rounded-xl text-[#2D1B3D] text-lg font-medium placeholder:text-[#8C7A9C] resize-none"
                rows={4}
                data-testid="contact-note-input"
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-full btn-magic py-7 text-xl font-bold mt-4"
              data-testid="contact-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-6 h-6 mr-3" />
                  Send to Karine
                </>
              )}
            </Button>
          </form>
        </motion.div>

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center mt-10"
        >
          <p className="text-[#4A3A5A] text-lg mb-4">Or reach out directly:</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="tel:678-665-4712" 
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white px-6 py-3 rounded-full font-bold hover:shadow-lg hover:scale-105 transition-all"
            >
              <Phone className="w-5 h-5" />
              678-665-4712
            </a>
            <a 
              href="mailto:karine@intuitivematchmaker.com" 
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#B8860B] text-white px-6 py-3 rounded-full font-bold hover:shadow-lg hover:scale-105 transition-all"
            >
              <Mail className="w-5 h-5" />
              Email Karine
            </a>
          </div>
        </motion.div>

        <Footer />
      </main>
    </div>
  );
};

export default ContactPage;
